/** PersonaResultCard — the /profile command's "I AM A …" archetype card. */
import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/apiClient';
import { useSession } from '@/features/auth/hooks/useSession';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import { PersonaCard } from '../PersonaCard';
import type { PersonaInputs } from '../PersonaCard';
import type { TripRow } from '../TravelReport';
import type { ReservationRow } from '../ReservationsReport';

const DAY = 86_400_000;

interface Trip {
  destination: string;
  dateRange: string;
  city?: string;
  lodging?: string;
  /** Real trip dates (ISO) — preferred for past/upcoming classification. */
  startISO?: string;
  endISO?: string;
}

/**
 * Classify discovery travelTrips into upcoming vs recent — the job emits a
 * flat list with year-less dateRange strings ("Mar 23", "Jun 25–26"), so the
 * card was blindly calling the first one "Upcoming" (a March trip in June).
 * Parses the FIRST month+day in the range against today; a date more than
 * ~6 months in the past is assumed to be next year (Dec → Jan boundary).
 * Unparseable dates are treated as recent (never falsely promised as a plan).
 */
function classifyTrips<T extends Trip>(trips: T[], now = new Date()): { upcoming: T[]; recent: T[] } {
  const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const upcoming: T[] = [];
  const recent: T[] = [];
  for (const t of trips) {
    // Real dates when the job provides them — the yearless dateRange parse
    // below misreads "Jul 8" from LAST July as next July once the scan
    // window exceeds ~6 months.
    if (t.startISO) {
      const d = new Date(t.startISO).getTime();
      if (!Number.isNaN(d)) {
        ((t.endISO ? new Date(t.endISO).getTime() : d) >= today ? upcoming : recent).push(t);
        continue;
      }
    }
    const m = /([A-Za-z]{3,9})\s+(\d{1,2})/.exec(t.dateRange ?? '');
    const monthIdx = m ? MONTHS.indexOf(m[1].slice(0, 3).toLowerCase()) : -1;
    if (monthIdx === -1) { recent.push(t); continue; }
    let d = new Date(now.getFullYear(), monthIdx, Number(m![2])).getTime();
    if (d < today - 180 * DAY) d = new Date(now.getFullYear() + 1, monthIdx, Number(m![2])).getTime();
    (d >= today ? upcoming : recent).push(t);
  }
  return { upcoming, recent };
}

interface DiscoveryStatusSlim {
  jobs?: {
    calendar?: { travelTrips?: TripRow[]; restaurantReservations?: ReservationRow[] };
    inbox?: { restaurantReservations?: ReservationRow[] };
    sent?: { sentTopics?: Array<{ topic: string; count: number }>; innerCircleSize?: number; contactCount?: number; circle?: { regulars?: number } };
  };
}

export function PersonaResultCard() {
  const [inputs, setInputs] = useState<PersonaInputs | null | undefined>(undefined);
  const { token } = useSession();

  useEffect(() => {
    if (!token) return;
    let alive = true;
    (async () => {
      try {
        const wsId = getActiveWorkspaceId();
        const data = await apiFetch<DiscoveryStatusSlim>(
          `/workspaces/${wsId}/onboarding/discovery-status`,
          { authToken: token },
        );
        const allTrips = data?.jobs?.calendar?.travelTrips ?? [];
        const { recent, upcoming } = classifyTrips(allTrips);
        const trips: TripRow[] = [...recent, ...upcoming.map((t) => ({ ...t, planned: true }))];
        const calRes = data?.jobs?.calendar?.restaurantReservations ?? [];
        const mailRes = data?.jobs?.inbox?.restaurantReservations ?? [];
        const calKeys = new Set(calRes.map((r) => `${r.name.toLowerCase()}|${new Date(r.whenISO).toDateString()}`));
        const reservations = [
          ...calRes,
          ...mailRes.filter((r) => !calKeys.has(`${r.name.toLowerCase()}|${new Date(r.whenISO).toDateString()}`)),
        ];
        if (alive) {
          setInputs({
            trips,
            reservations,
            lodgings: allTrips.map((t) => t.lodging).filter(Boolean) as string[],
            sentTopics: data?.jobs?.sent?.sentTopics ?? [],
            innerCircleSize: data?.jobs?.sent?.innerCircleSize,
            // circle.regulars (rollup-derived) beats the sample contactCount
            contactCount: data?.jobs?.sent?.circle?.regulars ?? data?.jobs?.sent?.contactCount,
          });
        }
      } catch {
        if (alive) setInputs(null);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (inputs === undefined) return null;
  if (!inputs || (inputs.trips.length === 0 && inputs.reservations.length === 0)) {
    return <p>Not enough data for your card yet — connect your accounts and let a sync finish.</p>;
  }
  return <PersonaCard inputs={inputs} />;
}
